import { ConfigProvider, Modal } from 'antd'
import zhCN from 'antd/lib/locale/zh_CN'
import CryptoJS from 'crypto-js'
import i18n from 'i18next'
import { match, pinyin } from 'pinyin-pro'
import { ReactElement, ReactNode, Suspense, useState } from 'react'
import ReactDOM from 'react-dom/client'
import * as uuid from 'uuid'
import { cmsApi } from '@/api'
import Coupon from '@/components/Coupon'
import PlanModal from '@/components/PlanModal'
import { UserStore } from '@/global-states'
import { initAudioService } from './WebSocketAudioService'

const env = import.meta.env
const videSuffix = '?x-oss-process=video/snapshot,t_0,m_fast,ar_auto'

export const getLanguage = () => {
  if (localStorage.getItem('test_language')) {
    return 'en'
  }
  return 'zh'
}

export const isZh = () => {
  return getLanguage() === 'zh'
}

export const urlSource = (url: string, type?: string) => {
  let suffix = ''
  if (type === 'video' && isVideo(url)) {
    suffix = videSuffix
  }

  if (url?.indexOf('http') > -1 || url?.indexOf('//') > -1) {
    return `${url}${suffix}`
  }

  return `${env.VITE_API_BASE_URL_CDN}${url}${suffix}`
}

export const isVideo = (url: string) => {
  return /\.(mp4|mov|avi|wmv|flv|webm|mkv|3gp|mpeg|mpg|m4v)$/i.test(url)
}

export const urlOsskey = (osskey: string) => {
  return `${env.VITE_API_BASE_URL_CDN}${osskey.replace('public/', '')}`
}

export const tuple = <T extends any[]>(...elements: T) => {
  return elements
}

export const Render: (_: { children: () => ReactNode }) => ReactElement = (props: any) => <>{props.children()}</>

export const downlad = (urls: string, fileName: string) => {
  const x = new window.XMLHttpRequest()
  x.open('GET', urls, true)
  x.responseType = 'blob'

  x.onload = () => {
    const url = window.URL.createObjectURL(x.response)
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.download = fileName
    a.style.display = 'none'
    document.body.append(a)
    a.click()
    a.remove()
  }
  x.send()
  return x
}

export const findClosest = (arr: number[], target: number): number => {
  let closestIndex = 0
  let minDifference = Math.abs(arr[0] - target)

  for (let i = 1; i < arr.length; i++) {
    const difference = Math.abs(arr[i] - target)
    if (difference < minDifference) {
      minDifference = difference
      closestIndex = i
    }
  }

  return closestIndex
}

export const isMobile = () => {
  const userAgent: string = navigator.userAgent
  const mobileKeywords: string[] = ['Android', 'iPhone', 'iPad', 'Windows Phone', 'Mobile']
  const isMobile = mobileKeywords.some(function (keyword) {
    return userAgent.indexOf(keyword) !== -1
  })
  return isMobile
}

export const eventTracking = (event_name: string, properties = {}) => {
  const { uid } = UserStore
  let device_id = UserStore.device_id
  const tracked = localStorage.getItem('event_track')

  if (!device_id) {
    device_id = `di${uuid.v4()}`
    localStorage.setItem('di', `${device_id}`)
    UserStore.device_id = device_id
  }

  const track = {
    event_name,
    di: device_id,
    client_time: parseInt((Date.now() / 1000).toFixed()),
    properties: {
      page_route: location.href,
      uid: uid,
      platform: 'web',
      ...properties
    }
  } as any

  if (!tracked) {
    firstEventTracking(JSON.parse(JSON.stringify(track)))
  }
  track.properties = JSON.stringify(track.properties)
  localStorage.setItem('event_track', '1')
  return cmsApi.post('event_logs', track)
}

export const firstEventTracking = (track: any) => {
  const { userPackage } = UserStore
  track.properties.system = navigator.userAgent
  track.properties.invite_code = sessionStorage.getItem('code') || userPackage?.invitor_aff_code
  track.properties.refer = document?.referrer

  let channel
  const code = sessionStorage.getItem('code')
  const promoterCode = sessionStorage.getItem('promoterCode')
  const bd_vid = sessionStorage.getItem('bd_vid')
  const msclkid = sessionStorage.getItem('msclkid')
  const ad_vid = bd_vid || msclkid
  if (bd_vid) {
    channel = 'sem_bd'
  } else if (msclkid) {
    channel = 'sem_bing'
  } else if (code) {
    channel = 'invite'
  } else if (promoterCode) {
    channel = 'promote'
  }

  track.properties.channel = channel

  if (ad_vid) {
    track.properties.ad_vid = ad_vid
  }

  cmsApi.post('event_logs', {
    ...track,
    event_name: 'first_event_track',
    properties: JSON.stringify(track.properties)
  })
}

export const Encrypt = (word: string) => {
  const key = CryptoJS.enc.Utf8.parse('GHJjPa9YBDaDZtzx')
  const iv = CryptoJS.enc.Utf8.parse('0')
  const encrypted = CryptoJS.AES.encrypt(word, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  })
  return encrypted.ciphertext.toString(CryptoJS.enc.Base64)
}

export const showPlanModal = () => {
  if (UserStore.planModalInstance) {
    UserStore.planModalInstance.setOpen(true)
    return
  }

  const div = document.createElement('div')
  document.body.appendChild(div)

  const root = ReactDOM.createRoot(div)

  root.render(
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#6828a8' // 设置主题色
        }
      }}
    >
      <Suspense>
        <Render>
          {function showPlanModal() {
            const [open, setOpen] = useState(true)

            UserStore.planModalInstance = { setOpen }

            return (
              <PlanModal open={open} onCancel={() => setOpen(false)} onSuccess={() => UserStore.updateUserPackage()} />
            )
          }}
        </Render>
      </Suspense>
    </ConfigProvider>
  )
}

export const showCouponModal = () => {
  if (UserStore.couponModalInstance) {
    UserStore.couponModalInstance.setOpen(true)
    return
  }

  const div = document.createElement('div')
  document.body.appendChild(div)

  const root = ReactDOM.createRoot(div)

  root.render(
    <Suspense>
      <Render>
        {function showPlanModal() {
          const [open, setOpen] = useState(true)

          UserStore.couponModalInstance = { setOpen }

          return <Coupon open={open} onCancel={() => setOpen(false)} />
        }}
      </Render>
    </Suspense>
  )
}

export const demoVideos = [
  {
    url: 'hf/local/demo-videos/1.mp4',
    ossKey: 'public/hf/local/demo-videos/1.mp4'
  },
  {
    url: 'hf/local/demo-videos/2.mp4',
    ossKey: 'public/hf/local/demo-videos/2.mp4'
  },
  {
    url: 'hf/local/demo-videos/3.mp4',
    ossKey: 'public/hf/local/demo-videos/3.mp4'
  }
]

export const getdemoAudios = () => {
  return [
    {
      id: 1,
      name: i18n.t('utils.demo_audio_1'),
      url: 'hf/local/demo-audios/1.wav',
      ossKey: 'public/hf/local/demo-audios/1.wav'
    },
    {
      id: 2,
      name: i18n.t('utils.demo_audio_2'),
      url: 'hf/local/demo-audios/2.wav',
      ossKey: 'public/hf/local/demo-audios/2.wav'
    },
    {
      id: 3,
      name: i18n.t('utils.demo_audio_3'),
      url: 'hf/local/demo-audios/3.wav',
      ossKey: 'public/hf/local/demo-audios/3.wav'
    }
  ]
}

export const translateHtml = (text: string) => {
  if (text) {
    return text
      .replace(/<\/div>/g, '')
      .replace(/<div>/g, '\n')
      .replace(/<br>/g, '\n')
      .trim()
      .replace(/<(?!\/?(break|phoneme|category)\b)[^>]+>/g, '')
      .replace(/<break\s+class="[^"]*"\s+time="([^"]*)"\s+time-text="[^"]*"\s*>/g, '<break time="$1">')
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '')
      .replace(/&nbsp;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/<(?!break\b[^>]*>)(?!\/break>)/g, '')
      .replace(/(<\/?break\b[^>]*>)|>/g, (match: any, p: any) => {
        // 如果匹配的是 <break> 或 </break>，则保留它
        if (p) return p
        // 否则，移除 '>'
        return ''
      })
  }
  return text
}

export const showDeclare = () => {
  return new Promise((resolve, reject) => {
    let index = 3

    const instance = Modal.confirm({
      width: isZh() ? 600 : 800,
      title: i18n.t('utils.declare_title'),
      content: (
        <>
          <div>{i18n.t('utils.declare_content_1')}</div>
          <ul className="declare-list">
            <li>{i18n.t('utils.declare_content_2')}</li>
            <li>{i18n.t('utils.declare_content_3')}</li>
            <li>
              {i18n.t('utils.declare_content_4')}
              <label className="red">{i18n.t('utils.declare_warning_1')}</label>
              {i18n.t('utils.declare_content_5')}
            </li>
            <li>
              {i18n.t('utils.declare_content_6')}
              <label className="red">{i18n.t('utils.declare_warning_2')}</label>
            </li>
            <li>
              {i18n.t('utils.declare_content_7')}
              <a target="_blank" href="/eula.html">
                {i18n.t('utils.user_agreement')}
              </a>
              、
              <a target="_blank" href="/privacy_agreement.html">
                {i18n.t('utils.privacy_agreement')}
              </a>
              。
            </li>
          </ul>
        </>
      ),
      okText: (
        <div>
          {i18n.t('utils.declare_agree')}
          {index > 0 && <label style={{ display: 'inline-block', width: 36 }}>（{index}s）</label>}
        </div>
      ),
      cancelText: i18n.t('utils.cancel'),
      okButtonProps: {
        disabled: true
      },
      onOk: () => {
        resolve(true)
        UserStore.declareChecked = true
      },
      onCancel: () => {
        reject()
        UserStore.declareChecked = false
      }
    })

    const si = setInterval(() => {
      index = index - 1
      instance.update({
        okText: (
          <div>
            {i18n.t('utils.declare_agree')}
            {index > 0 ? <label style={{ display: 'inline-block', width: 36 }}>（{index}s）</label> : ''}
          </div>
        )
      })
      if (index < 1) {
        instance.update({
          okButtonProps: {
            disabled: false
          }
        })
        clearInterval(si)
      }
    }, 1000)
  })
}

export const getRandomTexts = () => {
  return [
    {
      key: 0,
      text: i18n.t('utils.random_text_0')
    },
    {
      key: 1,
      text: i18n.t('utils.random_text_1')
    },
    {
      key: 2,
      text: i18n.t('utils.random_text_2')
    },
    {
      key: 3,
      text: i18n.t('utils.random_text_3')
    },

    {
      key: 4,
      text: i18n.t('utils.random_text_4')
    },
    {
      key: 5,
      text: i18n.t('utils.random_text_5')
    },
    {
      key: 6,
      text: i18n.t('utils.random_text_6')
    },
    {
      key: 7,
      text: i18n.t('utils.random_text_7')
    },
    {
      key: 8,
      text: i18n.t('utils.random_text_8')
    }
  ]
}

export const getCreateFaceRandomTexts = () => {
  return [
    {
      key: 0,
      text: i18n.t('utils.face_text_0'),
      gender: 2
    },
    {
      key: 1,
      text: i18n.t('utils.face_text_1'),
      gender: 2
    },
    {
      key: 2,
      text: i18n.t('utils.face_text_2'),
      gender: 2
    },
    {
      key: 3,
      text: i18n.t('utils.face_text_3'),
      gender: 2
    },
    {
      key: 4,
      text: i18n.t('utils.face_text_4'),
      gender: 2
    },
    {
      key: 5,
      text: i18n.t('utils.face_text_5'),
      gender: 1
    },
    {
      key: 6,
      text: i18n.t('utils.face_text_6'),
      gender: 1
    },
    {
      key: 7,
      text: i18n.t('utils.face_text_7'),
      gender: 1
    },
    {
      key: 8,
      text: i18n.t('utils.face_text_8'),
      gender: 1
    }
  ]
}

export const getCreatePhotoRandomTexts = () => {
  return [
    {
      key: 0,
      text: i18n.t('utils.photo_text_0')
    },
    {
      key: 1,
      text: i18n.t('utils.photo_text_1')
    },
    {
      key: 2,
      text: i18n.t('utils.photo_text_2')
    },
    {
      key: 3,
      text: i18n.t('utils.photo_text_3')
    }
  ]
}

export const checkSensitive = (sensitives?: any[]) => {
  const errorWords: any[] = []
  const { sensitiveWords, sensitiveWordsPinyin, sensitiveWhiteList } = UserStore
  try {
    sensitives?.forEach((s) => {
      const platWords = translateHtml(s.words).replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '')
      const pinyinWords = pinyin(platWords, { type: 'array', toneType: 'none' })
      const pinyinWordsString = pinyinWords?.join('')
      // 精确匹配
      sensitiveWords?.forEach((word) => {
        if (word?.length && !errorWords.includes(word) && platWords.includes(word)) {
          if (!sensitiveWhiteList.includes(word)) {
            errorWords.push(word)
          }
        }
      })

      // 拼音校验
      sensitiveWordsPinyin?.forEach((word) => {
        if (word?.length && pinyinWordsString.includes(word)) {
          const matchs = match(platWords, word, { continuous: true, precision: 'every' })
          if (matchs?.length && !sensitiveWhiteList.includes(word)) {
            const matchsString = platWords.substring(matchs[0], (matchs.at(-1) || 0) + 1)
            !errorWords.includes(matchsString) && errorWords.push(matchsString)
          }
        }
      })
    })
  } catch (err) {
    console.log(err)
  }

  return errorWords
}

export const getAudioService = () => {
  const token = UserStore.getToken()
  const domain = env.VITE_API_BASE_URL_WEBSOCKET
  const audioService = initAudioService(`wss://${domain}/ws/app/v1/tts_voices/preview?token=${token}`)
  return audioService
}
